import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ClientInvoiceExportAcceptedResult } from '../../api/models/client-invoice-export-accepted-result';
import { ClientInvoiceExportDateField } from '../../api/models/client-invoice-export-date-field';
import { ClientInvoiceExportRequest } from '../../api/models/client-invoice-export-request';
import { ClientInvoiceExportSummary } from '../../api/models/client-invoice-export-summary';
import { ExportChangedEvent } from '../../api/models/export-changed-event';
import { ExportsService } from '../../api/services/exports.service';

@Injectable()
export class ClientExportsService {
  private readonly exportsApi = inject(ExportsService);

  private readonly exportsSubject = new BehaviorSubject<ClientInvoiceExportSummary[]>([]);
  public readonly exports$: Observable<ClientInvoiceExportSummary[]> = this.exportsSubject.asObservable();

  public seed(clientId: number): Observable<{ exports: ClientInvoiceExportSummary[] }> {
    return this.exportsApi.listClientInvoiceExports({ client_id: clientId }).pipe(
      tap((resp) => this.applySeedSnapshot(resp.exports)),
    );
  }

  // applySeedSnapshot merges the seed snapshot into the current
  // BehaviorSubject value keyed by export_id. The local copy is
  // preferred on conflict because it has already been patched by SSE
  // (which by definition is fresher than the seed snapshot taken at
  // request time). Without this, an SSE export_changed event arriving
  // during the seed RTT would be overwritten by the late seed response
  // — a brand-new export would disappear from the table for one cycle.
  private applySeedSnapshot(snapshot: ClientInvoiceExportSummary[]): void {
    const current = this.exportsSubject.value;
    if (current.length === 0) {
      this.exportsSubject.next(snapshot);
      return;
    }
    const currentByID = new Map(current.map((row) => [row.export_id, row]));
    const merged = snapshot.map((row) => currentByID.get(row.export_id) ?? row);
    const snapshotIDs = new Set(snapshot.map((row) => row.export_id));
    for (const row of current) {
      if (!snapshotIDs.has(row.export_id)) {
        merged.unshift(row);
      }
    }
    this.exportsSubject.next(merged);
  }

  public submit(clientId: number, body: ClientInvoiceExportRequest): Observable<ClientInvoiceExportAcceptedResult> {
    return this.exportsApi.createClientInvoiceExport({ client_id: clientId, body });
  }

  // Applies an SSE export_changed event to the local list: appends new
  // rows at the top (newest first) and updates existing rows in place,
  // keyed by export_id. The event payload mirrors the summary shape
  // closely enough that we never need a follow-up GET.
  public applyEvent(event: ExportChangedEvent): void {
    const current = this.exportsSubject.value;
    const idx = current.findIndex((row) => row.export_id === event.export_id);
    const patched = this.eventToSummary(event, idx >= 0 ? current[idx] : null);
    if (idx >= 0) {
      const next = [...current];
      next[idx] = patched;
      this.exportsSubject.next(next);
    } else {
      this.exportsSubject.next([patched, ...current]);
    }
  }

  private eventToSummary(
    event: ExportChangedEvent,
    previous: ClientInvoiceExportSummary | null,
  ): ClientInvoiceExportSummary {
    return {
      export_id: event.export_id,
      kind: event.kind,
      // Prefer the parameters carried on the event itself — the backend
      // includes them on the `pending` event so a brand-new row renders
      // its Scope column immediately. Fall back to the snapshot we
      // already hold for later events that omit them, and finally to an
      // empty filter that the next list refresh fills in.
      parameters: event.parameters ??
        previous?.parameters ?? {
          scopes: [],
          date_field: ClientInvoiceExportDateField.Created,
          from: '',
          to: '',
        },
      requested_by: event.requested_by,
      requested_at: event.requested_at,
      status: event.status,
      size_bytes: event.size_bytes,
      invoice_count: event.invoice_count,
      attachment_count: event.attachment_count,
      expires_at: event.expires_at,
      completed_at: event.completed_at,
      failed_at: event.failed_at,
      failure_reason: event.failure_reason,
    };
  }
}
